'use client';

import { Button } from '@/components/ui/button';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/navigation';
import { Component, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Next.js signals notFound() / redirect() / HTTP fallbacks via the `digest`
// field on the thrown error. They are not real errors — rethrow so the
// nearest framework boundary can render not-found.tsx / error.tsx or perform
// the redirect. Without this, a client ErrorBoundary swallows them and
// renders its fallback forever (paid-video guest 404 case).
const NEXT_FRAMEWORK_DIGEST_PREFIXES = [
  'NEXT_NOT_FOUND',
  'NEXT_REDIRECT',
  'NEXT_HTTP_ERROR_FALLBACK',
] as const;

function isFrameworkError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const digest = (error as { digest?: unknown }).digest;
  if (typeof digest !== 'string') return false;
  return NEXT_FRAMEWORK_DIGEST_PREFIXES.some((prefix) => digest.startsWith(prefix));
}

/**
 * Error boundary component to catch React errors and display user-friendly messages.
 */
class ErrorBoundaryClass extends Component<
  ErrorBoundaryProps & { router: ReturnType<typeof useRouter> },
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps & { router: ReturnType<typeof useRouter> }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    if (isFrameworkError(error)) throw error;
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (isFrameworkError(error)) return;

    Sentry.captureException(error, {
      level: 'fatal',
      tags: { component: 'ErrorBoundary' },
      extra: { componentStack: errorInfo.componentStack },
    });

    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleGoBack = (): void => {
    const { router } = this.props;
    if (typeof document !== 'undefined' && document.referrer) {
      router.push(document.referrer);
    } else {
      router.back();
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-dvh items-center justify-center bg-background p-4">
          <div className="max-w-md w-full rounded-lg bg-surface border border-border p-6 text-center">
            <h2 className="text-xl font-semibold text-text-primary mb-2">Something went wrong</h2>
            <p className="text-text-secondary mb-4">
              We encountered an unexpected error. Please try refreshing the page.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={this.handleReset} variant="outline">
                Try Again
              </Button>
              <Button onClick={this.handleGoBack} variant="outline">
                Go Back
              </Button>
              <Button onClick={() => globalThis.location.reload()} variant="default">
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Error boundary wrapper that provides router access.
 */
export function ErrorBoundary(props: ErrorBoundaryProps) {
  const router = useRouter();
  return <ErrorBoundaryClass {...props} router={router} />;
}
